import React, {useEffect, useState} from 'react';
import {InputAdornment} from "@mui/material";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import DeleteModal from "../../../Modals/DeleteModal";
import ButtonAction from "../../../../components/buttons/ButtonAction";
import {AddIcon, EmailSubsectionChip, TrashOutlineIcon} from "../../../../components/buttons/IconButtons";
import ButtonIcon from "../../../../components/buttons/ButtonIcon";
import {isEmailFormat} from "../../../../Apis/functions";
import {CustomTextField} from "../../../../components/CustomTextField";
import {BUILDING_WHITE_LIST_MANUAL_MODEL} from "../../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Manual tab of the white list modal, it captures by hand the residents
 * authorized to register in the building.
 *
 * @param {boolean} props.emptyFields - Empty field validation flag.
 * @param {Function} props.setEmptyFields - Sets the empty field validation flag.
 * @param {Function} props.setDisplayModal - Hides the white list modal while the delete one is open.
 * @param {Object} props.whiteListDetail - Form model holding the manual white list.
 * @param {Function} props.setWhiteListDetail - Updates the form model.
 */
const ResidentWhiteListManual = (
    {
        emptyFields,
        setEmptyFields,
        setDisplayModal,
        whiteListDetail,
        setWhiteListDetail
    }
) => {
    const {t} = useTranslation();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedResident, setSelectedResident] = useState(null);

    // Only the delete modal is left on screen, the white list one behind it is hidden
    useEffect(() => {
        setDisplayModal?.(showDeleteModal ? 'd-none' : '');
    }, [showDeleteModal]);

    /**
     * Updates a specific resident of the white list.
     *
     * Modifies only the field being edited while preserving the rest of the list.
     *
     * @param {Object} e - Input change event.
     * @param {number} index - Index of the whitelist item to update.
     */
    const handleChangeTemp = (e, index) => {
        const { name, value } = e.target;

        setWhiteListDetail((prev) => {
            const whiteListManual = [...prev.whiteListManual];

            whiteListManual[index] = {
                ...whiteListManual[index],
                [name]: value,
            };

            return {
                ...prev,
                whiteListManual,
            };
        });
    };

    /**
     * Adds a new empty resident to the white list.
     *
     * Before adding, validates the current whitelist using whiteListDetail.isValidWhiteList().
     * If invalid, triggers empty field validation state.
     *
     * @returns {void}
     */
    const handleAdd = () => {
        if(!whiteListDetail?.isValidWhiteList()){
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        setWhiteListDetail(prev => ({
            ...prev,
            whiteListManual: [...prev.whiteListManual, { ...BUILDING_WHITE_LIST_MANUAL_MODEL, id: `${Date.now()}-${Math.random()}` }]
        }));
    };

    /**
     * Removes a resident from the white list.
     *
     * Also displays a success toast after deletion.
     *
     * @param {Object} item - White list row to remove.
     * @param {string|number} item.id - Unique identifier of the white list row.
     */
    const handleDeleteItem = (item) => {
        setWhiteListDetail((prev) => ({
            ...prev,
            whiteListManual: prev.whiteListManual.filter((currentItem) => currentItem.id !== item.id),
        }));
        toast.success(t('has_been_deleted_successfully'));
    };

    return (
        <>
            <div className={"col-12 my-4"}>
                    { whiteListDetail?.whiteListManual?.map((e, index) => {
                        return <div className={"col-12 p-0 mb-3"} key={e?.id ?? index}>
                            <div className={"row border-full-shades-300 background-shades-50 border-radius-8px input-padding-top"}>
                                <div className={"col-sm-12 col-md-3 px-1"}>
                                    <CustomTextField
                                        id={`txt_rwlm_name_${index}`}
                                        length={128}
                                        name={'name'}
                                        value={e.name}
                                        trans={'name'}
                                        type={'text'}
                                        disabled={false}
                                        onChange={(event) => handleChangeTemp(event, index)}
                                        emptyFields={emptyFields}
                                        required={true}
                                        placeholder={t("enter_name")}
                                    />
                                </div>
                                <div className={"col-sm-12 col-md-3 px-1"}>
                                    <CustomTextField
                                        id={`txt_rwlm_last_name_${index}`}
                                        length={128}
                                        name={'lastName'}
                                        value={e.lastName}
                                        trans={'last_name'}
                                        type={'text'}
                                        disabled={false}
                                        onChange={(event) => handleChangeTemp(event, index)}
                                        emptyFields={emptyFields}
                                        required={true}
                                        placeholder={t("enter_last_name")}
                                    />
                                </div>

                                <div className={"col-sm-12 col-md-3 px-1"}>
                                    <CustomTextField
                                        id={`txt_rwlm_email_${index}`}
                                        length={128}
                                        name={'email'}
                                        value={e.email}
                                        trans={'email'}
                                        type={'text'}
                                        onChange={(event) => handleChangeTemp(event, index)}
                                        emptyFields={emptyFields}
                                        required={true}
                                        disabled={false}
                                        isValid={(!e?.email || isEmailFormat(e.email))}
                                        isValidText={'email_format_invalid'}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <EmailSubsectionChip className={'w-h-24px'} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        placeholder={t("enter_email")}
                                    />
                                </div>

                                <div className={"col-sm-12 col-md-3 px-1"}>
                                    <div className={"d-flex"}>
                                        <div className={"w-100"}>
                                            <CustomTextField
                                                id={`txt_rwlm_unit_${index}`}
                                                length={128}
                                                name={'unit'}
                                                value={e.unit}
                                                trans={'unit'}
                                                type={'text'}
                                                disabled={false}
                                                onChange={(event) => handleChangeTemp(event, index)}
                                                emptyFields={emptyFields}
                                                required={true}
                                                placeholder={t("enter_apartment")}
                                            />
                                        </div>

                                        { whiteListDetail?.whiteListManual?.length > 1 &&
                                            <div className={"pl-3"}>
                                                <ButtonIcon
                                                    id={`btn_rwlm_delete_${index}`}
                                                    Icon={TrashOutlineIcon}
                                                    onClick={() => {
                                                        setSelectedResident(e);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className='btn-outlined-error-600 h-40px w-40px'
                                                    iconProps={'btn-icon-svg-24px'}
                                                />
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    })}

                    <div className='col-12 px-0'>
                        <ButtonAction
                            id='btn_rwlm_add_resident'
                            name='add_resident'
                            Icon={AddIcon}
                            splitName={false}
                            className='btn-outlined-secondary-600-00'
                            disabled={false}
                            onClick={() => {
                                handleAdd();
                            }}
                        />
                    </div>
            </div>

            <DeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setSelectedResident(null);
                }}
                data={[selectedResident]}
                onClickFn={() => {
                    if (selectedResident !== null) {
                        handleDeleteItem(selectedResident);
                    }
                    setShowDeleteModal(false);
                    setSelectedResident(null);
                }}
                title_modal={'delete_resident'}
                element_modal={'resident'}
            />
        </>
    )
}


export default ResidentWhiteListManual
